import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { clientService } from '@/services/client-service';
import type { Client } from '@/types/core/client';
import type { Page } from '@/types/shared/page';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { clientQueryKeys } from './query-keys';

/**
 * List clients for a given tenant.
 */
export function useListClients(
  tenantId: UUID,
  opts?: {
    includeRevoked?: boolean;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Client>, unknown>, Error> {
  // The clients list API returns every client in one array (no server-side
  // limit/filter/next), so the hook exposes only includeRevoked. Options the
  // service can't honor were previously kept on the signature and the query key,
  // which handed a caller passing { filter } a distinct cache entry silently
  // holding the unfiltered list.
  return useInfiniteQuery<
    Page<Client>,
    Error,
    InfiniteData<Page<Client>, unknown>
  >({
    queryKey: clientQueryKeys.list(tenantId, opts?.includeRevoked),
    queryFn: () =>
      clientService.listClients(tenantId, {
        includeRevoked: opts?.includeRevoked,
      }),
    enabled: !isEmptyUUID(tenantId),
    initialPageParam: undefined as undefined,
    getNextPageParam: () => undefined,
    getPreviousPageParam: () => undefined,
  });
}
